import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContentContainer,
  Search,
  Text,
  Checkbox,
  Divider,
  Icon,
  Avatar,
} from '@vibe/core';
import { Team as TeamIcon, DropdownChevronDown, Person } from '@vibe/icons';
import type { GroupByMode } from './AssigneeTeamToggle';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TeamOption {
  id: number;
  name: string;
  /** User names belonging to this team (for description display) */
  memberNames?: string[];
}

interface UserOption {
  id: number;
  name: string;
  avatarUrl?: string;
}

interface ResourceSelectionDropdownProps {
  /** Current resource type mode — determines what the dropdown shows */
  groupByMode: GroupByMode;
  /** Available teams to choose from */
  teamOptions: TeamOption[];
  /** Available users to choose from (used when groupByMode is 'assignees') */
  userOptions: UserOption[];
  /** Currently selected team IDs */
  selectedTeamIds: number[];
  /** Currently selected user IDs (for 'assignees' mode) */
  selectedUserIds: number[];
  /** Called when team selection changes */
  onTeamSelectionChange: (teamIds: number[]) => void;
  /** Called when user selection changes */
  onUserSelectionChange: (userIds: number[]) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Searchable multi-select dropdown for selecting which teams or users
 * to display in the scheduler. Shows teams when mode is 'teams' or
 * 'teamWithUsers', shows users when mode is 'assignees'.
 *
 * Matches the reference UI: search bar, checkbox list with team/user icons,
 * optional team member description.
 */
const ResourceSelectionDropdown: React.FC<ResourceSelectionDropdownProps> = ({
  groupByMode,
  teamOptions,
  userOptions,
  selectedTeamIds,
  selectedUserIds,
  onTeamSelectionChange,
  onUserSelectionChange,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const lowerSearch = searchValue.toLowerCase();

  const showTeams = groupByMode === 'teams' || groupByMode === 'teamWithUsers';
  const showUsers = groupByMode === 'assignees';

  // ── Team toggling ─────────────────────────────────────────────────────────

  const toggleTeam = useCallback(
    (id: number) => {
      const next = selectedTeamIds.includes(id)
        ? selectedTeamIds.filter((t) => t !== id)
        : [...selectedTeamIds, id];
      onTeamSelectionChange(next);
    },
    [selectedTeamIds, onTeamSelectionChange],
  );

  // ── User toggling ─────────────────────────────────────────────────────────

  const toggleUser = useCallback(
    (id: number) => {
      const next = selectedUserIds.includes(id)
        ? selectedUserIds.filter((u) => u !== id)
        : [...selectedUserIds, id];
      onUserSelectionChange(next);
    },
    [selectedUserIds, onUserSelectionChange],
  );

  // ── Search filtering ──────────────────────────────────────────────────────

  const filteredTeams = useMemo(
    () =>
      teamOptions.filter((t) => t.name.toLowerCase().includes(lowerSearch)),
    [teamOptions, lowerSearch],
  );

  const filteredUsers = useMemo(
    () =>
      userOptions.filter((u) => u.name.toLowerCase().includes(lowerSearch)),
    [userOptions, lowerSearch],
  );

  // ── Trigger label ─────────────────────────────────────────────────────────

  const triggerLabel = useMemo(() => {
    const userCount = selectedUserIds.length;
    const teamCount = selectedTeamIds.length;
    const total = userCount + teamCount;

    if (total === 0) {
      return showTeams ? 'Select teams' : 'Select resources';
    }

    // Single selection — show the name
    if (total === 1) {
      if (userCount === 1) {
        const user = userOptions.find((u) => u.id === selectedUserIds[0]);
        return user?.name ?? '1 user';
      }
      const team = teamOptions.find((t) => t.id === selectedTeamIds[0]);
      return team?.name ?? '1 team';
    }

    // Multiple selections — show combined count
    const parts: string[] = [];
    if (userCount > 0) parts.push(`${userCount} user${userCount > 1 ? 's' : ''}`);
    if (teamCount > 0) parts.push(`${teamCount} team${teamCount > 1 ? 's' : ''}`);
    return parts.join(', ');
  }, [showTeams, selectedTeamIds, selectedUserIds, teamOptions, userOptions]);

  return (
    <div className="rp-resource-selection-dropdown">
      <Dialog
        zIndex={1000}
        content={
          <DialogContentContainer
            className="rp-resource-selection-dropdown__content"
            size={DialogContentContainer.sizes.MEDIUM}
          >
            {/* Search */}
            <div className="rp-resource-selection-dropdown__search">
              <Search
                size="small"
                value={searchValue}
                onChange={(value: string) => setSearchValue(value)}
                placeholder="Search person/team name, position or skill"
              />
            </div>

            <Divider />

            {/* List — show Users and/or Teams sections based on mode */}
            <div className="rp-resource-selection-dropdown__list">
              {/* Users section — hidden in teams-only mode */}
              {showUsers && (
                <>
                  <Text
                    type={Text.types.TEXT2}
                    className="rp-resource-selection-dropdown__section-title"
                  >
                    Users
                  </Text>
                  {filteredUsers.length === 0 && (
                    <Text
                      type={Text.types.TEXT2}
                      className="rp-resource-selection-dropdown__empty"
                    >
                      No users found
                    </Text>
                  )}
                  {filteredUsers.map((user) => (
                    <label
                      key={user.id}
                      className="rp-resource-selection-dropdown__option"
                    >
                      <Checkbox
                        checked={selectedUserIds.includes(user.id)}
                        onChange={() => toggleUser(user.id)}
                      />
                      {user.avatarUrl ? (
                        <Avatar
                          type="img"
                          src={user.avatarUrl}
                          size="small"
                          ariaLabel={user.name}
                        />
                      ) : (
                        <Avatar
                          type="text"
                          text={user.name}
                          size="small"
                          ariaLabel={user.name}
                        />
                      )}
                      <Text type={Text.types.TEXT2}>{user.name}</Text>
                    </label>
                  ))}

                  <Divider className="rp-resource-selection-dropdown__section-divider" />
                </>
              )}

              {/* Teams section */}
              <Text
                type={Text.types.TEXT2}
                className="rp-resource-selection-dropdown__section-title"
              >
                Teams
              </Text>
              {filteredTeams.length === 0 && (
                <Text
                  type={Text.types.TEXT2}
                  className="rp-resource-selection-dropdown__empty"
                >
                  No teams found
                </Text>
              )}
              {filteredTeams.map((team) => (
                <label
                  key={team.id}
                  className="rp-resource-selection-dropdown__option"
                >
                  <Checkbox
                    checked={selectedTeamIds.includes(team.id)}
                    onChange={() => toggleTeam(team.id)}
                  />
                  <Icon
                    icon={TeamIcon}
                    iconSize={18}
                    className="rp-resource-selection-dropdown__option-icon"
                  />
                  <div className="rp-resource-selection-dropdown__option-info">
                    <Text type={Text.types.TEXT2}>{team.name}</Text>
                    {team.memberNames && team.memberNames.length > 0 && (
                      <Text
                        type={Text.types.TEXT2}
                        className="rp-resource-selection-dropdown__option-desc"
                      >
                        {team.memberNames.join(', ')}
                      </Text>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </DialogContentContainer>
        }
        hideTrigger={['clickoutside', 'esckey']}
        showTrigger={['click']}
        position="bottom-start"
      >
        <Button
          size={Button.sizes.SMALL}
          kind={Button.kinds.TERTIARY}
          className="rp-resource-selection-dropdown__trigger"
        >
          <Icon
            icon={showTeams ? TeamIcon : Person}
            iconSize={16}
            className="rp-resource-selection-dropdown__trigger-icon"
          />
          <span>{triggerLabel}</span>
          <Icon
            icon={DropdownChevronDown}
            iconSize={16}
            className="rp-resource-selection-dropdown__chevron"
          />
        </Button>
      </Dialog>
    </div>
  );
};

export default ResourceSelectionDropdown;
